import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import { toast } from 'sonner';
import { 
  Shield, 
  Lock, 
  Unlock,
  AlertTriangle,
  Check
} from 'lucide-react';
import { Switch } from '../components/ui/switch';

// Partner logo colors
const PARTNER_COLORS = {
  gcash: '#007DFE',
  maya: '#00D084',
  grabpay: '#00B14F',
  bdo: '#0066B3',
  bpi: '#A81C32',
  unionbank: '#FF6B00',
};

export default function PartnersPage() {
  const { user, api } = useAuth();
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(null);

  const fetchPartners = useCallback(async () => {
    try {
      const response = await api.get('/partners');
      setPartners(response.data.partners);
    } catch (error) {
      toast.error('Failed to load partners');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchPartners();
  }, [fetchPartners]);

  const handleToggleProtection = async (partnerId, currentProtected) => {
    if (user?.panic_mode) {
      toast.error('Cannot modify protection during panic mode');
      return;
    }

    setToggling(partnerId);
    try {
      await api.put(`/partners/${partnerId}/toggle`, {
        partner_id: partnerId,
        protected: !currentProtected,
      });
      
      setPartners(partners.map(p => 
        p.id === partnerId ? { ...p, protected: !currentProtected } : p
      ));
      
      toast.success(`Protection ${!currentProtected ? 'enabled' : 'disabled'}`);
    } catch (error) {
      toast.error('Failed to update protection');
    } finally {
      setToggling(null);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="spinner w-12 h-12"></div>
        </div>
      </Layout>
    );
  }

  const protectedCount = partners.filter(p => p.protected).length;
  const lockedCount = partners.filter(p => p.locked).length;

  return (
    <Layout>
      <div className="space-y-8" data-testid="partners-page">
        {/* Header */}
        <div>
          <h1 className="text-3xl lg:text-4xl font-black text-[#E6F1FF]">Partner Apps</h1>
          <p className="text-[#8892B0] mt-1">Manage protection for your connected accounts</p>
        </div>

        {/* Stats */}
        <div className="flex flex-wrap gap-4">
          <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#2BBBAD]/20 border border-[#2BBBAD]/30">
            <Shield size={16} className="text-[#2BBBAD]" />
            <span className="text-sm text-[#E6F1FF]">{protectedCount} Protected</span>
          </div>
          {lockedCount > 0 && (
            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#FF3B30]/20 border border-[#FF3B30]/30">
              <Lock size={16} className="text-[#FF3B30]" />
              <span className="text-sm text-[#FF3B30]">{lockedCount} Locked</span>
            </div>
          )}
        </div>

        {/* Partners Grid */}
        <div className="partners-grid">
          {partners.map((partner, index) => (
            <div 
              key={partner.id}
              className={`
                partner-card animate-slide-up relative
                ${partner.locked ? 'locked' : ''}
              `}
              style={{ animationDelay: `${index * 50}ms` }}
              data-testid={`partner-card-${partner.code}`}
            >
              {/* Status badges */}
              <div className="absolute top-4 right-4 flex gap-2">
                {partner.protected && !partner.locked && (
                  <div className="protected-badge">
                    <Check size={12} />
                    Protected
                  </div>
                )}
                {partner.locked && (
                  <div className="flex items-center gap-1 px-2 py-1 rounded-md bg-[#FF3B30]/20 border border-[#FF3B30]/30">
                    <Lock size={12} className="text-[#FF3B30]" />
                    <span className="text-xs font-semibold text-[#FF3B30]">Locked</span>
                  </div>
                )}
              </div>

              {/* Partner Logo */}
              <div 
                className="partner-logo mb-4"
                style={{ backgroundColor: PARTNER_COLORS[partner.code] || '#2BBBAD' }}
              >
                {partner.name.charAt(0)}
              </div>

              {/* Partner Info */}
              <h3 className="text-xl font-bold text-[#E6F1FF] mb-1">{partner.name}</h3>
              <p className="text-sm text-[#8892B0] mb-6">
                {partner.locked 
                  ? 'Account is currently locked' 
                  : partner.protected 
                    ? 'Protected by PanicLock' 
                    : 'Not protected'}
              </p>

              {/* Protection Toggle */}
              <div className="flex items-center justify-between pt-4 border-t border-white/5">
                <span className="text-sm text-[#8892B0]">Protection</span>
                <Switch
                  checked={partner.protected}
                  disabled={partner.locked || toggling === partner.id || user?.panic_mode}
                  onCheckedChange={() => handleToggleProtection(partner.id, partner.protected)}
                  className="data-[state=checked]:bg-[#2BBBAD]"
                  data-testid={`partner-toggle-${partner.code}`}
                />
              </div>

              {/* Locked status */}
              {partner.locked && partner.locked_at && (
                <div className="mt-4 p-3 rounded-lg bg-[#FF3B30]/10 border border-[#FF3B30]/20">
                  <div className="flex items-center gap-2">
                    <AlertTriangle size={14} className="text-[#FF3B30]" />
                    <span className="text-xs text-[#FF3B30]">
                      Locked at {new Date(partner.locked_at).toLocaleString()}
                    </span>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Info Box */}
        <div className="glass-card rounded-2xl p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#2BBBAD]/20 flex items-center justify-center flex-shrink-0">
              <Shield className="w-6 h-6 text-[#2BBBAD]" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-[#E6F1FF] mb-2">How Protection Works</h3>
              <ul className="space-y-2 text-sm text-[#8892B0]">
                <li className="flex items-start gap-2">
                  <Check size={16} className="text-[#2BBBAD] flex-shrink-0 mt-0.5" />
                  <span>Enable protection on the accounts you want to secure</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check size={16} className="text-[#2BBBAD] flex-shrink-0 mt-0.5" />
                  <span>When PanicLock is activated, all protected accounts are instantly locked</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check size={16} className="text-[#2BBBAD] flex-shrink-0 mt-0.5" />
                  <span>Recovery requires verification and unlocking each account individually</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
